import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlayer } from "../../PlayerContext";
import httpClient from "../utils/httpClients";

// ---------- QUEUE ITEM (NO DRAG, NO CROSS FOR CURRENT) ----------
function QueueItem({ track, index, onPlay, onRemove, formatTime, isCurrent }) {
  const truncatedTitle =
    track.title && track.title.length > 15
      ? track.title.substring(0, 15) + "..."
      : track.title || "Unknown Title";

  const truncatedArtist =
    track.artist && track.artist.length > 12
      ? track.artist.substring(0, 12) + "..."
      : track.artist || "Unknown Artist";

  return (
    <div
      className={`d-flex align-items-center gap-3 p-3 rounded mb-2 cursor-pointer transition-all select-none ${
        isCurrent
          ? "bg-primary text-white shadow-lg"
          : "bg-dark bg-opacity-50 hover-bg"
      }`}
      onClick={() => onPlay(index)}
    >
      {/* Cover */}
      <div
        className="flex-shrink-0 rounded shadow-sm"
        style={{
          width: 48,
          height: 48,
          backgroundImage: `url(${track.coverImageUrl || "/assets/images/dashboard/01.png"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Title + Artist */}
      <div className="flex-grow-1 min-w-0 me-3">
        <p className="mb-1 small text-truncate fw-semibold">
          {truncatedTitle}
          {isCurrent && (
            <span className="ms-2">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          )}
          {track.isVideo && (
            <span className="badge bg-warning text-dark ms-2 small">VIDEO</span>
          )}
        </p>
        <p
          className="mb-0 text-muted xsmall text-truncate"
          title={track.artist}
        >
          {truncatedArtist}
        </p>
      </div>

      {/* Duration */}
      <div className="flex-shrink-0 text-end" style={{ width: "50px" }}>
        <span className="text-muted small">
          {formatTime(track.duration || 0)}
        </span>
      </div>

      {/* Remove Button – ONLY IF NOT CURRENT */}
      {!isCurrent && (
        <div className="flex-shrink-0 ms-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
            className="btn btn-sm p-1 rounded hover-bg-danger text-white opacity-70 hover:opacity-100"
            title="Remove from queue"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- MAIN PLAYER COMPONENT ----------
function Player() {
  const navigate = useNavigate();
  const {
    tracks,
    currentTrack,
    currentTrackIndex,
    seek,
    duration,
    volume,
    isShuffle,
    isRepeat,
    isPlaying,
    isLoading,
    togglePlayPause,
    handleNextTrack,
    handlePrevTrack,
    handleSeekChange,
    handleVolumeChange,
    toggleShuffle,
    toggleRepeat,
    playMediaByIndex,
    triggerQueueUpdate,
  } = usePlayer();

  const [showQueue, setShowQueue] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const noTrack = !currentTrack;
  const isVideoPlaying = currentTrack?.isVideo;

  // Navigate to track page
  const handleTrackClick = () => {
    if (!currentTrack) return;
    const trackId = currentTrack._id;
    const title = (currentTrack.title || "track")
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");
    navigate(`/${title}/${trackId}`);
  };

  // Play from queue
  const jumpToTrack = (index) => {
    playMediaByIndex(index);
  };

  // Remove from queue (never the current track)
  const removeFromQueue = async (index) => {
    const track = tracks[index];
    if (!track) return;
    try {
      await httpClient.post(
        "user/queue/queue-controller",
        {
          action: "remove",
          trackId: track._id,
          mediaType: track.isVideo ? "video" : "audio",
        },
        { skipLoader: true, skipErrorToast: true },
      );
      triggerQueueUpdate();
    } catch (err) {
      console.error("Remove failed:", err);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.code === "Space" && !isVideoPlaying) {
        e.preventDefault();
        togglePlayPause();
      } else if (e.code === "ArrowRight") handleNextTrack();
      else if (e.code === "ArrowLeft") handlePrevTrack();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [togglePlayPause, handleNextTrack, handlePrevTrack, isVideoPlaying]);

  return (
    <>
      {/* PLAYER FOOTER – 100% SAME */}
      <footer
        className="footer player-footer bg-dark text-white border-top border-secondary position-fixed bottom-0 start-0 end-0"
        style={{ zIndex: 1030 }}
      >
        <div className="footer-top py-1 bg-black bg-opacity-50">
          <div className="container-fluid px-4">
            <input
              type="range"
              className="seek-slider w-100"
              min="0"
              max={duration || 100}
              step="0.1"
              value={Number.isFinite(seek) ? seek : 0}
              onChange={(e) => {
                if (noTrack || isVideoPlaying) return;
                const next = parseFloat(e.target.value);
                if (!Number.isFinite(next)) return;
                const clamped = Math.max(0, Math.min(next, duration || 0));
                handleSeekChange(clamped);
              }}
              disabled={noTrack || isVideoPlaying}
              style={{
                background: `linear-gradient(to right, #007bff ${(seek / (duration || 100)) * 100}%, #444 ${(seek / (duration || 100)) * 100}%)`,
              }}
            />
          </div>
        </div>

        <div className="footer-bottom py-3">
          <div className="container-fluid px-4">
            <div className="row align-items-center">
              {/* Track Info */}
              <div className="col-xl-4 col-md-4 col-12 mb-3 mb-md-0">
                <div
                  className="d-flex align-items-center gap-3"
                  onClick={handleTrackClick}
                  style={{ cursor: currentTrack ? "pointer" : "default" }}
                  role="button"
                >
                  <div
                    className="track-cover rounded shadow-sm flex-shrink-0"
                    style={{
                      width: 56,
                      height: 56,
                      backgroundImage: `url(${currentTrack?.coverImageUrl || `${process.env.PUBLIC_URL}/img/live/songDefault.png`})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div className="track-info text-start flex-grow-1 min-w-0">
                    <h6 className="track-title mb-1 text-white text-truncate fw-semibold">
                      {currentTrack?.title || "No Track"}
                      {isVideoPlaying && (
                        <span className="badge bg-warning text-dark ms-2 small">
                          VIDEO
                        </span>
                      )}
                    </h6>
                    <p className="track-artist text-muted small mb-0 text-truncate">
                      {currentTrack?.artist || ""}
                    </p>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="col-xl-4 col-md-4 col-12">
                <div className="d-flex align-items-center justify-content-center gap-4 position-relative">
                  {/* Loader overlay when loading */}
                  {isLoading && (
                    <div
                      className="position-absolute"
                      style={{
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 10,
                      }}
                    >
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={toggleShuffle}
                    disabled={noTrack || isVideoPlaying || isLoading}
                    className={`btn p-2 ${isShuffle ? "text-primary" : "text-white"} ${isLoading ? "opacity-50" : ""}`}
                    title={isShuffle ? "Shuffle ON" : "Shuffle OFF"}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <g>
                        <path
                          d="M6.67969 6.358L5.69109 5.36941C5.30132 4.97784 4.83776 4.66747 4.32723 4.45626C3.81671 4.24505 3.26936 4.13719 2.71687 4.13894H0.849022C0.625246 4.13894 0.410634 4.22783 0.252401 4.38607C0.0941667 4.5443 0.00527191 4.75891 0.00527191 4.98269C0.00527191 5.20646 0.0941667 5.42107 0.252401 5.57931C0.410634 5.73754 0.625246 5.82644 0.849022 5.82644H2.71512C3.04606 5.82554 3.3739 5.89033 3.67962 6.01706C3.98535 6.14379 4.26288 6.32994 4.49613 6.56472L5.48437 7.5512C5.6426 7.70971 5.85732 7.79887 6.08128 7.79907C6.30525 7.79927 6.52012 7.71049 6.67863 7.55226C6.83714 7.39403 6.9263 7.17931 6.9265 6.95535C6.9267 6.73138 6.83791 6.51651 6.67969 6.358Z"
                          fill="currentColor"
                        ></path>
                        <path
                          d="M5.68336 12.6299L11.7506 6.56259C11.9839 6.32784 12.2614 6.14172 12.5672 6.01499C12.8729 5.88826 13.2007 5.82345 13.5316 5.82431H15.1281L14.1529 6.79954C14.0742 6.87793 14.0118 6.97107 13.9692 7.07363C13.9266 7.17619 13.9047 7.28615 13.9047 7.3972C13.9047 7.50825 13.9266 7.61821 13.9692 7.72076C14.0118 7.82332 14.0742 7.91647 14.1529 7.99486C14.3111 8.15308 14.5257 8.24196 14.7495 8.24196C14.9732 8.24196 15.1878 8.15308 15.3461 7.99486L17.7465 5.59579C17.8271 5.51525 17.8911 5.41961 17.9347 5.31435C17.9783 5.20909 18.0008 5.09626 18.0008 4.98232C18.0008 4.86837 17.9783 4.75555 17.9347 4.65028C17.8911 4.54502 17.8271 4.44938 17.7465 4.36884L15.3461 1.96872C15.1898 1.81251 14.9785 1.72382 14.7576 1.72172C14.5366 1.71961 14.3237 1.80426 14.1645 1.95747C13.8234 2.28548 13.8315 2.84095 14.1662 3.17564L15.127 4.13646H13.5316C12.9792 4.13475 12.4318 4.24263 11.9213 4.45383C11.4108 4.66504 10.9472 4.97539 10.5574 5.36693L4.49016 11.4363C4.2569 11.6711 3.97937 11.8572 3.67365 11.984C3.36792 12.1107 3.04009 12.1755 2.70914 12.1746H0.84375C0.619974 12.1746 0.405362 12.2635 0.247129 12.4217C0.0888948 12.5799 0 12.7946 0 13.0183C0 13.2421 0.0888948 13.4567 0.247129 13.615C0.405362 13.7732 0.619974 13.8621 0.84375 13.8621H2.70914C3.26174 13.8635 3.80913 13.7553 4.31965 13.5438C4.83016 13.3323 5.29367 13.0217 5.68336 12.6299Z"
                          fill="currentColor"
                        ></path>
                        <path
                          d="M15.3461 16.0311L17.7465 13.6317C17.8271 13.5512 17.891 13.4556 17.9347 13.3504C17.9783 13.2451 18.0007 13.1323 18.0007 13.0184C18.0007 12.9045 17.9783 12.7917 17.9347 12.6865C17.891 12.5812 17.8271 12.4856 17.7465 12.4051L15.3461 10.0053C15.1898 9.84913 14.9785 9.76044 14.7576 9.75834C14.5366 9.75624 14.3237 9.84088 14.1645 9.99409C13.8234 10.3221 13.8315 10.8776 14.1662 11.2123L15.127 12.1731H13.5376C13.2067 12.1739 12.8789 12.1091 12.5731 11.9824C12.2674 11.8557 11.9899 11.6695 11.7566 11.4348L10.6625 10.3407C10.5844 10.2613 10.4914 10.1982 10.3887 10.1549C10.2861 10.1117 10.1759 10.0892 10.0645 10.0887C9.95314 10.0883 9.84277 10.1099 9.73978 10.1523C9.63679 10.1947 9.54322 10.2571 9.46446 10.3359C9.3857 10.4146 9.32331 10.5082 9.2809 10.6112C9.23849 10.7142 9.21688 10.8245 9.21734 10.9359C9.21779 11.0473 9.24029 11.1575 9.28354 11.2601C9.32679 11.3628 9.38994 11.4558 9.46934 11.5339L10.5634 12.6284C10.9533 13.0198 11.4168 13.3301 11.9273 13.5413C12.4378 13.7525 12.9852 13.8604 13.5376 13.8588H15.1277L14.1529 14.8358C13.9946 14.994 13.9057 15.2086 13.9057 15.4324C13.9057 15.6562 13.9946 15.8708 14.1529 16.029C14.3108 16.1875 14.5253 16.2768 14.749 16.2772C14.9728 16.2776 15.1875 16.1891 15.3461 16.0311Z"
                          fill="currentColor"
                        ></path>
                      </g>
                      <defs>
                        <clipPath>
                          <rect width="18" height="18" fill="white"></rect>
                        </clipPath>
                      </defs>
                    </svg>
                  </button>

                  <button
                    onClick={handlePrevTrack}
                    disabled={noTrack || isLoading}
                    className={`btn p-2 text-white ${isLoading ? "opacity-50" : ""}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6 8V16L18 12L6 8Z"
                        fill="currentColor"
                        transform="scale(-1,1) translate(-24,0)"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={togglePlayPause}
                    disabled={noTrack || isLoading}
                    className={`btn btn-primary rounded-circle p-3 shadow-lg ${isLoading ? "opacity-50" : ""}`}
                  >
                    {isLoading ? (
                      <div
                        className="spinner-border spinner-border-sm text-white"
                        role="status"
                        style={{ width: "20px", height: "20px" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : isPlaying ? (
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="white"
                      >
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="white"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={handleNextTrack}
                    disabled={noTrack || isLoading}
                    className={`btn p-2 text-white ${isLoading ? "opacity-50" : ""}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M6 8V16L18 12L6 8Z" fill="currentColor" />
                    </svg>
                  </button>

                  <button
                    onClick={toggleRepeat}
                    disabled={noTrack || isVideoPlaying}
                    className={`btn p-2 ${isRepeat ? "text-primary" : "text-white"}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M17 2l4 4-4 4"></path>
                      <path d="M3 11v-1a4 4 0 0 1 4-4h14"></path>
                      <path d="M7 22l-4-4 4-4"></path>
                      <path d="M21 13v1a4 4 0 0 1-4 4H3"></path>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Right Controls */}
              <div className="col-xl-4 col-md-4 col-12 mt-3 mt-md-0">
                <div className="row align-items-center justify-content-end gx-3">
                  <div className="col-auto">
                    <div className="d-flex align-items-center gap-2">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                      </svg>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={Number.isFinite(volume) ? volume : 0}
                        onChange={(e) =>
                          handleVolumeChange(parseFloat(e.target.value))
                        }
                        className="volume-slider"
                        style={{ width: 80 }}
                        disabled={noTrack}
                      />
                      <span className="small">{Math.round(volume * 100)}%</span>
                    </div>
                  </div>

                  <div className="col-auto text-center">
                    <span className="small text-white">
                      {formatTime(seek)} / {formatTime(duration)}
                    </span>
                  </div>

                  <div className="col-auto">
                    <button
                      onClick={() => setShowQueue(!showQueue)}
                      className={`btn p-2 ${showQueue ? "text-primary" : "text-white"}`}
                      title="Queue"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M8 6h13" strokeLinecap="round" />
                        <path d="M8 12h13" strokeLinecap="round" />
                        <path d="M8 18h13" strokeLinecap="round" />
                        <path
                          d="M3 6h.01"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <path
                          d="M3 12h.01"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <path
                          d="M3 18h.01"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* <div className="col-auto">
                    <button
                      onClick={() =>
                        document.documentElement.requestFullscreen()
                      }
                      className="btn p-2 text-white"
                      title="Fullscreen"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
                        <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
                        <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
                        <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
                      </svg>
                    </button>
                  </div> */}
                  <div className="col-auto">
                    <button
                      onClick={toggleFullscreen}
                      className="btn p-2 text-white"
                      title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                    >
                      {isFullscreen ? (
                        // 🔽 MINIMIZE ICON
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M8 3v5H3"></path>
                          <path d="M21 8h-5V3"></path>
                          <path d="M3 16h5v5"></path>
                          <path d="M16 21v-5h5"></path>
                        </svg>
                      ) : (
                        // 🔼 FULLSCREEN ICON
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
                          <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
                          <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
                          <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* QUEUE SIDEBAR – NO DRAG, NO CROSS FOR CURRENT */}
      {showQueue && (
        <>
          <div
            className="position-fixed end-0 top-0 h-100 bg-dark text-white p-4 shadow-lg"
            style={{
              width: "450px",
              zIndex: 1050,
              overflowY: "auto",
              borderLeft: "1px solid #333",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0 fw-bold text-white">
                Queue ({tracks.length})
              </h5>
              <button
                onClick={() => setShowQueue(false)}
                className="btn btn-sm btn-close btn-close-white"
                aria-label="Close"
              />
            </div>

            <div className="queue-list">
              {tracks.length === 0 ? (
                <p className="text-muted text-center mt-5">Queue is empty</p>
              ) : (
                tracks.map((track, index) => (
                  <QueueItem
                    key={track._id}
                    track={track}
                    index={index}
                    onPlay={jumpToTrack}
                    onRemove={removeFromQueue}
                    formatTime={formatTime}
                    isCurrent={index === currentTrackIndex}
                  />
                ))
              )}
            </div>
          </div>

          <div
            className="position-fixed inset-0 bg-black bg-opacity-50"
            style={{ zIndex: 1040 }}
            onClick={() => setShowQueue(false)}
          />
        </>
      )}
    </>
  );
}

export default Player;
